import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hardship apply · public front for the backend endpoints that have been
 * live since bake #9. Two flows in one page: early UBI (paid work at
 * $1-$3/hour when someone needs it) and product discount (25%, 50%, or 100%
 * off any bundle for verified hardship).
 */
export default function HardshipPage() {
  const [mode, setMode] = useState("ubi"); // "ubi" | "discount"
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    email: "", name: "", city: "", state: "",
    situation: "", story: "",
    tier: "standard", // ubi tier
    discount_tier: "hardship-25", // discount tier
    product_slug: "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/sage/hardship/info`).then(r => r.json()).then(setInfo).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.email.trim() || !form.story.trim() || form.story.length < 20) {
      toast.error("Please share your email and a short story (at least 20 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const url = mode === "ubi"
        ? `${API}/api/sage/hardship/ubi/apply`
        : `${API}/api/sage/hardship/discount/apply`;
      const body = mode === "ubi"
        ? {
            email: form.email, name: form.name, city: form.city, state: form.state,
            situation: form.situation, story: form.story, requested_tier: form.tier,
          }
        : {
            email: form.email, name: form.name, city: form.city, state: form.state,
            situation: form.situation, story: form.story,
            requested_tier: form.discount_tier, product_slug: form.product_slug,
          };
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setSubmitted(data);
      toast.success("Application filed.");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e] flex items-center justify-center px-6" data-testid="hardship-thanks">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-serif text-[#2c3e2a] border-b-2 border-[#b58c2a] pb-4">
            Application filed.
          </h1>
          <p className="mt-6 text-lg font-serif text-[#4a5a48]">
            {submitted.message || "Someone will review your application within seven days."}
          </p>
          {submitted.application_id && (
            <p className="mt-3 text-sm font-serif italic text-[#7c8a75]" data-testid="application-id">
              Your reference: {submitted.application_id}
            </p>
          )}
          <a href="/" className="inline-block mt-8 border border-[#2c3e2a] py-2 px-5 rounded font-serif hover:bg-[#2c3e2a] hover:text-[#fbfaf6] transition" data-testid="back-home-link">
            Back home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="hardship-page">
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">If you're going through it, apply here.</span>
        </div>
        <p className="mt-6 text-lg font-serif text-[#4a5a48] max-w-3xl">
          Two paths, side by side. One puts you to work (small paid tasks at one to three dollars an hour when
          you need it). The other cuts the price of the tools (twenty-five, fifty, or one hundred percent off
          for verified hardship). Pick either, or apply for both.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-6" data-testid="mode-picker">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => { setMode("ubi"); setSubmitted(null); }}
            className={
              mode === "ubi"
                ? "bg-[#2c3e2a] text-[#fbfaf6] py-3 px-6 rounded font-serif"
                : "border border-[#d9d3bd] bg-white text-[#2c3e2a] py-3 px-6 rounded font-serif hover:border-[#2c3e2a] transition"
            }
            data-testid="mode-ubi"
          >
            Apply for paid work (early UBI)
          </button>
          <button
            type="button"
            onClick={() => { setMode("discount"); setSubmitted(null); }}
            className={
              mode === "discount"
                ? "bg-[#2c3e2a] text-[#fbfaf6] py-3 px-6 rounded font-serif"
                : "border border-[#d9d3bd] bg-white text-[#2c3e2a] py-3 px-6 rounded font-serif hover:border-[#2c3e2a] transition"
            }
            data-testid="mode-discount"
          >
            Apply for a product discount
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-8">
        <div className="bg-[#efeadc] border border-[#d9d3bd] rounded p-5" data-testid="mode-explanation">
          {mode === "ubi" ? (
            <div>
              <div className="text-lg font-serif font-bold text-[#2c3e2a]">Paid work (early UBI)</div>
              <p className="text-sm font-serif text-[#4a5a48] mt-2">
                Standard tier pays one dollar an hour for small tasks (proof-reading, tagging, testing, quick
                answers). Extreme tier pays three dollars an hour for people in acute crisis (homeless, no
                shelter, actively fleeing). Paid to the Rice-the-World till or straight to a bank the day it clears
                the rolling thirty-day window. No paperwork bar.
              </p>
            </div>
          ) : (
            <div>
              <div className="text-lg font-serif font-bold text-[#2c3e2a]">Product discount</div>
              <p className="text-sm font-serif text-[#4a5a48] mt-2">
                Twenty-five percent off for hardship, fifty percent for deep hardship, one hundred percent free
                for people in acute crisis. Any product, any bundle. Every free grant counts as a Baker and Sage
                donation on the public ledger.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16" data-testid="apply-form">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email">
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-email"
            />
          </Field>
          <Field label="Your name (optional)">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-name"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="City">
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-city"
              />
            </Field>
            <Field label="State">
              <input
                type="text"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                maxLength={5}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-state"
              />
            </Field>
          </div>
          <Field label="Your situation (short — homeless, disabled, unemployed, in-between, etc.)">
            <input
              type="text"
              value={form.situation}
              onChange={(e) => setForm({ ...form, situation: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-situation"
            />
          </Field>
          <Field label="Your story (short paragraph — anything the person reading this should know)">
            <textarea
              required
              rows={4}
              value={form.story}
              onChange={(e) => setForm({ ...form, story: e.target.value })}
              className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
              data-testid="input-story"
            />
          </Field>
          {mode === "ubi" ? (
            <Field label="Tier">
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                data-testid="input-tier"
              >
                <option value="standard">Standard · $1/hour</option>
                <option value="extreme">Extreme · $3/hour (acute crisis)</option>
              </select>
            </Field>
          ) : (
            <>
              <Field label="Discount tier">
                <select
                  value={form.discount_tier}
                  onChange={(e) => setForm({ ...form, discount_tier: e.target.value })}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                  data-testid="input-discount-tier"
                >
                  <option value="hardship-25">Hardship · 25% off</option>
                  <option value="hardship-50">Deep hardship · 50% off</option>
                  <option value="hardship-100">Acute crisis · 100% off (free)</option>
                </select>
              </Field>
              <Field label="Product or bundle slug (e.g. sage, baker, entrepreneurs, buy-all)">
                <input
                  type="text"
                  value={form.product_slug}
                  onChange={(e) => setForm({ ...form, product_slug: e.target.value })}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white"
                  data-testid="input-product-slug"
                />
              </Field>
            </>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2c3e2a] text-[#fbfaf6] py-3 rounded font-serif hover:bg-[#3e4a3c] transition disabled:opacity-60"
            data-testid="submit-hardship"
          >
            {submitting ? "Sending…" : "Send my application"}
          </button>
        </form>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          The test is honesty, not paperwork. Every free grant counts as a Baker and Sage donation on the public ledger.
        </p>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-serif text-[#2c3e2a] mb-1">{label}</span>
      {children}
    </label>
  );
}
